import React, { useEffect, useState, Fragment, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select, Spin, Tooltip, Space, Typography, Row, Col } from 'antd';
import {
  InfoCircleOutlined,
  DeleteOutlined,
  LoadingOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import EmailSegmentationRules from './EmailSegmentationRules';
import usePrivateAxios from '../hooks/usePrivateAxios';
import GlobalColors from '../assets/colors/GlobalColors';
import StatusNotification from './StatusNotification';
import { SET_SELECTED_SEGMENTATION } from '../store/actions/types';

const { Option } = Select;
const { Text } = Typography;

const EmailSegmentations = () => {
  const [segmentations, setSegmentations] = useState([]);
  const [selectedSegmentation, setSelectedSegmentation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingSegmentation, setEditingSegmentation] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const { organizationId } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const axiosPrivate = usePrivateAxios();

  const fetchSegmentations = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axiosPrivate.get(`/api/segmentations/${organizationId}`);
      const fetchedSegmentations = response.data.segmentations || [];

      // Update both Redux store and local state
      dispatch({ type: 'SET_SEGMENTATIONS', data: fetchedSegmentations });
      setSegmentations(fetchedSegmentations);
    } catch (error) {
      console.error('Error fetching segmentations:', error);
      StatusNotification(
        'error',
        'Failed to Load Segmentations',
        'There was an error loading your segmentation filters. Please try again.',
        { error }
      );
    } finally {
      setIsLoading(false);
    }
  }, [axiosPrivate, organizationId, dispatch]);

  useEffect(() => {
    fetchSegmentations();
  }, [organizationId, fetchSegmentations]);

  // Get segmentations from Redux store
  const { segmentations: reduxSegmentations } = useSelector((state) => state.segmentationData);

  // Update local state when Redux store changes
  useEffect(() => {
    if (reduxSegmentations) {
      setSegmentations(reduxSegmentations);
    }
  }, [reduxSegmentations]);

  const handleChange = (value) => {
    setSelectedSegmentation(value);

    console.log({ value, segmentations });

    if (value) {
      // Find selected segmentation and update properties filter
      const segmentation = segmentations.find((seg) => seg.id === value);
      console.log('segmentation', segmentation);
      if (segmentation) {
        console.log('dispatching ');
        dispatch({
          type: SET_SELECTED_SEGMENTATION,
          data: segmentation,
        });
      }
    } else {
      // If selection is cleared, reset properties filter
      dispatch({
        type: SET_SELECTED_SEGMENTATION,
        data: null,
      });
    }
  };

  const handleDelete = async (segmentId) => {
    try {
      setIsDeleting(segmentId);
      await axiosPrivate.delete(`/api/segmentations/${segmentId}`);

      // Update Redux store
      dispatch({ type: 'REMOVE_SEGMENTATION', data: segmentId });

      // Clear selection if deleted
      if (selectedSegmentation === segmentId) {
        setSelectedSegmentation(null);
        dispatch({ type: 'SET_SELECTED_SEGMENTATION', data: null });
      }

      StatusNotification(
        'success',
        'Segmentation Deleted',
        'The segmentation filter has been deleted successfully.'
      );
    } catch (error) {
      console.error('Error deleting segmentation:', error);
      StatusNotification(
        'error',
        'Failed to Delete',
        'There was an error deleting the segmentation filter. Please try again.',
        { error }
      );
    } finally {
      setIsDeleting(null);
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '12px' }}>
        <Spin size='small' />
      </div>
    );
  }

  if (!segmentations?.length) {
    return (
      <div style={{ padding: '12px', textAlign: 'center' }}>
        <Text type='secondary'>No segmentation filters available</Text>
      </div>
    );
  }

  return (
    <>
      <Select
        style={{
          width: '100%',
          maxWidth: '600px',
        }}
        placeholder='Select a segmentation filter'
        value={selectedSegmentation}
        onChange={handleChange}
        allowClear
        loading={isLoading}
        dropdownStyle={{ padding: '4px' }}
      >
        {segmentations.map((segmentation) => (
          <Option key={segmentation.id} value={segmentation.id}>
            <Row justify='space-between' align='middle'>
              <Col>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '4px 0',
                  }}
                >
                  <Text style={{ fontSize: '14px' }}>{segmentation.name}</Text>
                  <Tooltip
                    placement='top'
                    color={GlobalColors.mainPurple}
                    title={segmentation.description}
                  >
                    <InfoCircleOutlined
                      style={{
                        fontSize: 14,
                        color: GlobalColors.mainPurple,
                        opacity: 0.8,
                      }}
                    />
                  </Tooltip>
                </span>
              </Col>
              <Col>
                <Space>
                  <Tooltip
                    placement='top'
                    color={GlobalColors.mainPurple}
                    title='Edit Segmentation'
                  >
                    <EditOutlined
                      style={{
                        color: GlobalColors.mainPurple,
                        fontSize: 14,
                        opacity: 0.8,
                        transition: 'opacity 0.2s',
                        marginRight: 8,
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                      onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.8')}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingSegmentation(segmentation);
                        setIsEditing(true);
                      }}
                    />
                  </Tooltip>
                  <Tooltip
                    placement='top'
                    color={GlobalColors.mainPurple}
                    title='Delete Segmentation'
                  >
                    {isDeleting === segmentation.id ? (
                      <LoadingOutlined
                        style={{
                          color: GlobalColors.mainPurple,
                          fontSize: 14,
                          opacity: 0.8,
                        }}
                      />
                    ) : (
                      <DeleteOutlined
                        style={{
                          color: GlobalColors.mainPurple,
                          fontSize: 14,
                          opacity: 0.8,
                          transition: 'opacity 0.2s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.8')}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(segmentation.id);
                        }}
                      />
                    )}
                  </Tooltip>
                </Space>
              </Col>
            </Row>
          </Option>
        ))}
      </Select>
      {isCreating && (
        <EmailSegmentationRules
          visible={isCreating}
          onClose={() => setIsCreating(false)}
          isEditing={false}
        />
      )}
      {isEditing && (
        <EmailSegmentationRules
          visible={isEditing}
          onClose={() => {
            setIsEditing(false);
            setEditingSegmentation(null);
          }}
          initialValues={editingSegmentation}
          isEditing={true}
        />
      )}
    </>
  );
};

export default EmailSegmentations;
